import { Eye, ChevronLeft, ChevronRight } from "@tamagui/lucide-icons";
import { useHomeownersWithHubsListView } from "common";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  YStack,
  XStack,
  Text,
  Button,
  Card,
  Paragraph,
  useMedia,
} from "tamagui";
import { Table } from "tamagui-config";

import SearchBox from "../../components/SearchBox";
import { Select } from "../../components/Select";
import { StatusPill } from "../../components/StatusPill";
// import { useSensorType } from "../../hooks/useSensorType";
import { joinTruthy } from "../../utils";


const CustomerList: React.FC = () => {
  const { t } = useTranslation();
  const { sm } = useMedia();
  // const { translateSensorType } = useSensorType();
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPageOptions = [8, 16, 24];
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [filter, setFilter] = useState<"all" | "critical">("all");
  const rowsPerPageSelectOptions = rowsPerPageOptions.map((n) => ({
    label: String(n),
    value: String(n),
  }));

  const {
    homeownerOverviews: customerOverviews,
    error,
    refetch,
  } = useHomeownersWithHubsListView() || {};

  const handleViewCustomer = (id: string) => {
    navigate(`/customers/${id}`);
  };

  const filteredCustomerOverviews = customerOverviews.filter((c) => {
    if (filter === "critical") return (c.criticalAlertCount || 0) > 0;
    return true;
  });

  const totalRequests = filteredCustomerOverviews.length;
  const totalPages = Math.ceil(totalRequests / rowsPerPage);
  const paginatedCustomerOverview = filteredCustomerOverviews.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage,
  );

  // Cell content shared by the desktop table and the phone cards.
  type Overview = (typeof paginatedCustomerOverview)[number];

  const renderCustomer = ({ customer }: Overview) => (
    <>
      <Text fontWeight="bold" fontSize="$4" paddingBottom="$1.5">
        {customer.name}
      </Text>
      <Text color="$gray10" fontSize="$4">
        {joinTruthy([customer.address, customer.city, customer.state], ", ") ||
          t("customers.unknownAddress")}
      </Text>
    </>
  );

  const renderSensors = ({ sensorCount, hubs }: Overview) => (
    <YStack gap="$1" alignItems="flex-start">
      <StatusPill outlined paddingHorizontal="$2" paddingVertical="$1.5">
        <Text fontSize="$4" color="$white12" textTransform="capitalize">
          {t("customers.sensorCount", {
            count: sensorCount,
          })}
        </Text>
      </StatusPill>
      {/* One customer can span several hubs (multi-hub households, shared
          bench units) — without this the summed count reads like one giant
          install. */}
      {(hubs?.length ?? 0) > 1 && (
        <Text color="$gray10" fontSize="$2">
          {t("customers.acrossHubs", {
            count: hubs.length,
          })}
        </Text>
      )}
    </YStack>
  );

  const renderAlerts = ({ criticalAlertCount }: Overview) =>
    criticalAlertCount >= 1 && (
      <StatusPill
        error={criticalAlertCount >= 1}
        paddingHorizontal="$2"
        paddingVertical="$1.5"
        borderRadius="$3"
      >
        {t("customers.statusCriticalAlerts", {
          count: criticalAlertCount,
        })}
      </StatusPill>
    );

  const renderView = ({ customer }: Overview) => (
    <Button
      icon={<Eye size={24} />}
      onPress={() => handleViewCustomer(customer.id)}
      size="$4"
      borderRadius="$14"
      borderColor="$hgPrimary"
      borderWidth="$1"
    >
      {t("common.view", "View")}
    </Button>
  );


  // if (loading) {
  //   return (
  //     <YStack flex={1} justifyContent="center" alignItems="center" padding="$4">
  //       <Spinner size="large" />
  //       <Text marginTop="$2">{t("common.loading")}</Text>
  //     </YStack>
  //   );
  // }

  if (error) {
    return (
      <YStack padding="$4">
        <Text color="$red10" fontSize="$5">
          {t("common.error")}
        </Text>
        {error && error.message && <Paragraph>{error.message}</Paragraph>}
      </YStack>
    );
  }

  return (
    <YStack flex={1} backgroundColor="$backgroundSoft">
      <XStack
        justifyContent="space-between"
        alignItems="center"
        marginBottom="$2"
        flexWrap="wrap"
        gap="$3"
      >
        <Text fontSize="$9" fontWeight="bold" color="$color">
          {t("sidebar.customers")}
        </Text>
        <Button
          theme="active"
          size="$4"
          onPress={() => navigate("/customers/new")}
        >
          {t("customers.createNewCustomer")}
        </Button>
      </XStack>
      <YStack space="$4" flex={1} paddingTop="$4">
        <Card padding="$4" elevation="$1">
          <YStack space="$3">
            <XStack
              flexWrap="wrap"
              space="$3"
              alignItems="center"
              justifyContent="space-between"
            >
              <YStack flex={1} minWidth={180}>
                <SearchBox
                  value={searchValue}
                  onChangeText={setSearchValue}
                  onSubmit={() => {
                    refetch({ text: searchValue });
                  }}
                />
              </YStack>
              <XStack alignItems="center" gap="$3.5">
                <Text fontSize="$4" fontWeight="700">
                  {t("customers.filterBy")}
                </Text>
                <Button
                  onPress={() =>
                    setFilter(filter === "critical" ? "all" : "critical")
                  }
                  borderRadius="$14"
                  borderColor={
                    filter === "critical" ? "$gray10" : "$transparent"
                  }
                  borderWidth={filter === "critical" ? 2 : 0}
                >
                  <Text fontSize="$4">{t("customers.criticalAlerts")}</Text>
                  <XStack
                    borderRadius="$14"
                    backgroundColor="$warning"
                    width="$2"
                    height="$1"
                    justifyContent="center"
                    alignItems="center"
                  >
                    {customerOverviews.reduce(
                      (sum, c) => sum + (c.criticalAlertCount || 0),
                      0,
                    )}
                  </XStack>
                </Button>
              </XStack>
            </XStack>
          </YStack>
        </Card>
        <Card elevation="$2">
          {sm ? (
            // Phone: one card per customer, same content as the table row.
            <YStack gap="$3" padding="$3">
              {paginatedCustomerOverview.length > 0 ? (
                paginatedCustomerOverview.map((overview) => (
                  <YStack
                    key={overview.customer.id}
                    tag="article"
                    gap="$2.5"
                    padding="$3.5"
                    borderRadius="$6"
                    borderWidth={1}
                    borderColor="$borderColor"
                    backgroundColor="$gray2"
                  >
                    <YStack>{renderCustomer(overview)}</YStack>
                    <XStack gap="$2" flexWrap="wrap" alignItems="flex-start">
                      {renderSensors(overview)}
                      {renderAlerts(overview)}
                    </XStack>
                    <XStack justifyContent="flex-end">
                      {renderView(overview)}
                    </XStack>
                  </YStack>
                ))
              ) : (
                <Text padding="$4" textAlign="center">
                  {t("customers.noResults")}
                </Text>
              )}
            </YStack>
          ) : (
            <Table>
              <Table.Head>
                <Table.Row rowLocation="first">
                  <Table.HeaderCell>
                    {t("customers.customerName")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t("customers.numberOfSensors")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t("customers.activeAlerts")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>{t("customers.action")}</Table.HeaderCell>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {paginatedCustomerOverview.length > 0 ? (
                  paginatedCustomerOverview.map((overview, idx) => (
                    <Table.Row
                      key={overview.customer.id}
                      rowLocation={
                        idx === 0 && paginatedCustomerOverview.length === 1
                          ? "first"
                          : idx === 0
                            ? "first"
                            : idx === paginatedCustomerOverview.length - 1
                              ? "last"
                              : "middle"
                      }
                    >
                      <Table.Cell>{renderCustomer(overview)}</Table.Cell>
                      <Table.Cell>{renderSensors(overview)}</Table.Cell>
                      <Table.Cell>
                        <XStack
                          gap="$2"
                          flexWrap="wrap"
                          alignItems="flex-start"
                          maxWidth="$12"
                        >
                          {renderAlerts(overview)}
                        </XStack>
                      </Table.Cell>
                      <Table.Cell>{renderView(overview)}</Table.Cell>
                    </Table.Row>
                  ))
                ) : (
                  <Table.Row rowLocation="first">
                    <Table.Cell>
                      <Text padding="$4" textAlign="center">
                        {t("customers.noResults")}
                      </Text>
                    </Table.Cell>
                    <Table.Cell />
                    <Table.Cell />
                    <Table.Cell />
                    <Table.Cell />
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          )}
          {totalRequests > 0 && (
            <XStack
              padding="$3.5"
              justifyContent="flex-end"
              alignItems="center"
              borderTopWidth={1}
              borderColor="$gray5"
              gap="$5"
              flexWrap="wrap"
              $xs={{ gap: "$3" }}
            >
              <Text fontSize="$3">{t("customers.rowsPerPage")}</Text>
              <Select
                options={rowsPerPageSelectOptions}
                value={String(rowsPerPage)}
                onValueChange={(value) => {
                  setRowsPerPage(Number(value));
                  setCurrentPage(0);
                }}
                placeholder={t("customers.rowsPerPage")}
                size="$3"
                chromeless
              />
              <Text fontSize="$3">
                {totalRequests === 0
                  ? "0"
                  : `${currentPage * rowsPerPage + 1}-${Math.min(
                      (currentPage + 1) * rowsPerPage,
                      totalRequests,
                    )} of ${totalRequests}`}
              </Text>

              <XStack gap="$3.5">
                <Button
                  icon={<ChevronLeft size={20} />}
                  aria-label={t("a11y.previousPage")}
                  disabled={currentPage === 0}
                  onPress={() => setCurrentPage(currentPage - 1)}
                  unstyled
                  backgroundColor="$transparent"
                  borderColor="$transparent"
                  width={20}
                  height={20}
                  padding={0}
                  $sm={{
                    width: 44,
                    height: 44,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                />
                <Button
                  iconAfter={<ChevronRight size={20} />}
                  aria-label={t("a11y.nextPage")}
                  disabled={currentPage >= totalPages - 1}
                  onPress={() => setCurrentPage(currentPage + 1)}
                  unstyled
                  backgroundColor="$transparent"
                  borderColor="$transparent"
                  width={20}
                  height={20}
                  padding={0}
                  $sm={{
                    width: 44,
                    height: 44,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                />
              </XStack>
            </XStack>
          )}
        </Card>
      </YStack>
    </YStack>
  );
};

export default CustomerList;
